"use client";
import { createContext, useContext, type ReactNode } from "react";

/**
 * Anchor nesting guard for the Studio renderers.
 *
 * HTML does not allow an <a> inside an <a>. When content nests two links (a
 * Link Wrapper around a body text whose rich text carries its own hyperlink:
 * /smile/dental-implants, master revision 75, four items of the icon row) the
 * server still emits both anchors, the browser's parser closes the outer one
 * where the inner opens, and React finds a tree it did not render: #418 on
 * every load and the whole <main> regenerated client-side (2026-09-11 /smile
 * production audit, finding 15). The content is TAG's, so the renderers guard
 * themselves: every anchor-emitting atom wraps its children in <AnchorScope>,
 * and an atom that finds itself inside one renders its inline fallback (a
 * <span> with the same classes and analytics attributes, no href, no role)
 * instead of a second anchor. The OUTER link is the one kept: it is the
 * element the visitor sees and clicks, and its click handler is the one that
 * reports the navigation, so a click on the inner text bubbles up and is
 * counted once. lib/utils/studio/nestedAnchors.ts reports the authored
 * nesting from the server so TAG can un-nest it.
 */
const AnchorScopeContext = createContext(false);

/** Marks the children of an anchor a renderer has just emitted. */
export const AnchorScope = ({ children }: { children: ReactNode }) => (
	<AnchorScopeContext.Provider value={true}>{children}</AnchorScopeContext.Provider>
);

/** True while rendering inside an anchor another renderer emitted. */
export const useInsideAnchor = (): boolean => useContext(AnchorScopeContext);

/**
 * The attribute the inline fallback carries, valued with the href it did not
 * render: the audit's SSR scan and the hydration specs count it, and a
 * stylesheet can target it (the Studio canvas keeps its "Nested Link" cue on
 * it, LinkWrapper.module.css).
 */
export const NESTED_LINK_ATTRIBUTE = "data-nested-link";
